
import { ReactElement, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTimedAccess } from '@/context/TimedAccessContext';
import * as logger from '@/utils/logger';

interface PremiumRouteProps {
  element: ReactElement;
}

const PremiumRoute = ({ element }: PremiumRouteProps) => {
  const { user, isSubscribed, isLoading, isPremiumLoading, subscriptionReason } = useAuth();
  const { hasFreeAccess } = useTimedAccess();
  
  logger.log("PremiumRoute - FULL DEBUG:", {
    user: user ? `User ID: ${user.id}` : 'No user',
    isSubscribed,
    hasFreeAccess,
    isLoading,
    isPremiumLoading,
    subscriptionReason,
    timestamp: new Date().toISOString()
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isPremiumLoading) {
      logger.log("PremiumRoute - Auth check completed:", {
        user: user ? `User ID: ${user.id}` : 'No user',
        isSubscribed,
        hasFreeAccess,
        subscriptionReason,
        path: location.pathname,
        timestamp: new Date().toISOString()
      });
      
      // Check if user is authenticated
      if (!user) {
        logger.log("PremiumRoute - User is null, redirecting to Login", {
          returnTo: location.pathname,
          reason: "User not authenticated",
          timestamp: new Date().toISOString()
        });
        // Use query parameter format for returnTo
        navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`);
      } 
      // Check if user has subscription or free access
      else if (!isSubscribed && !hasFreeAccess) {
        logger.log("PremiumRoute - User not subscribed and no free access, redirecting to premium", {
          userId: user.id,
          reason: subscriptionReason || "No active subscription",
          timestamp: new Date().toISOString()
        });
        navigate('/pricing');
      }
    }
  }, [user, isSubscribed, hasFreeAccess, isLoading, isPremiumLoading, subscriptionReason, navigate, location.pathname]);

  if (isLoading || isPremiumLoading) {
    logger.log("PremiumRoute - Loading state active", {
      isLoading,
      isPremiumLoading,
      timestamp: new Date().toISOString()
    });
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  logger.log("PremiumRoute - Rendering decision:", {
    isSubscribed,
    hasFreeAccess,
    subscriptionReason,
    renderingElement: (isSubscribed || hasFreeAccess) ? "Requested Element" : "Null",
    timestamp: new Date().toISOString()
  });
  
  // Allow access if user is subscribed OR has active free access
  return (isSubscribed || hasFreeAccess) ? element : null;
};

export default PremiumRoute;
